import {
  Target,
  FolderTree,
  Calendar,
  CalendarDays,
  CreditCard,
  AlertTriangle,
  TrendingDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FeatureLayout from '../../components/layout/FeatureLayout';
import SEOHead from '../../components/common/SEOHead';

const sectionKeys = [
  { icon: FolderTree, key: 'envelopes' },
  { icon: Calendar, key: 'monthly' },
  { icon: CalendarDays, key: 'weekly' },
  { icon: CreditCard, key: 'bankAccounts' },
  { icon: AlertTriangle, key: 'alerts' },
  { icon: TrendingDown, key: 'forecast' },
];

export default function FeatureBudgets() {
  const { t } = useTranslation('features');

  return (
    <FeatureLayout
      breadcrumbs={[{ label: t('budgets.title') }]}
    >
      <SEOHead
        title="Budgets prévisionnels et enveloppes"
        description="Définissez des budgets par catégorie avec suivi de progression. Enveloppes d'épargne anticipée, alertes de dépassement et prévisions d'impact sur votre solde."
        path="/fonctionnalites/budgets"
      />
      {/* Hero */}
      <section className="pub-hero py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="pub-ico w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Target className="w-8 h-8" />
          </div>
          <h1 className="pub-title text-3xl sm:text-4xl font-bold mb-4">
            {t('budgets.title')}
          </h1>
          <p className="pub-muted text-lg max-w-2xl mx-auto">
            {t('budgets.subtitle')}
          </p>
        </div>
      </section>

      {/* Détails */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid gap-8">
            {sectionKeys.map((section, index) => (
              <div
                key={section.key}
                className={`flex flex-col md:flex-row items-start gap-6 p-6 rounded-xl ${
                  index % 2 === 0 ? 'pub-card' : 'pub-section-alt'
                }`}
              >
                <div className="pub-ico w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="pub-title text-lg font-semibold mb-2">
                    {t(`budgets.sections.${section.key}.title`)}
                  </h3>
                  <p className="pub-muted">{t(`budgets.sections.${section.key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visuel code couleur */}
      <section className="pub-section-alt py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="pub-title text-2xl font-bold mb-8 text-center">
            {t('budgets.visualTracking')}
          </h2>
          <div className="grid sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="pub-card p-5 text-center">
              <div className="pub-track w-full h-3 rounded-full mb-4 overflow-hidden">
                <div className="pub-bar--ok h-full rounded-full" style={{ width: '45%' }} />
              </div>
              <p className="pub-state--ok text-sm font-medium">{t('budgets.onTrack')}</p>
              <p className="pub-subtle text-xs mt-1">{t('budgets.budgetUsed', { percent: 45 })}</p>
            </div>
            <div className="pub-card p-5 text-center">
              <div className="pub-track w-full h-3 rounded-full mb-4 overflow-hidden">
                <div className="pub-bar--warn h-full rounded-full" style={{ width: '78%' }} />
              </div>
              <p className="pub-state--warn text-sm font-medium">{t('budgets.warning')}</p>
              <p className="pub-subtle text-xs mt-1">{t('budgets.budgetUsed', { percent: 78 })}</p>
            </div>
            <div className="pub-card p-5 text-center">
              <div className="pub-track w-full h-3 rounded-full mb-4 overflow-hidden">
                <div className="pub-bar--over h-full rounded-full" style={{ width: '100%' }} />
              </div>
              <p className="pub-state--over text-sm font-medium">{t('budgets.exceeded')}</p>
              <p className="pub-subtle text-xs mt-1">{t('budgets.budgetUsed', { percent: 112 })}</p>
            </div>
          </div>
        </div>
      </section>
    </FeatureLayout>
  );
}
